import { Component, Element, h, Host, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { PictogramSchemeDefinition, PictogramSchemeName, PictogramSize } from "./constants/pictogram-types";
import {
  PICTOGRAM_SCHEME_CONFIG,
  PICTOGRAM_SCHEME_MAP,
  PICTOGRAM_SIZE_LARGE,
  PICTOGRAM_SIZE_NORMAL,
  PICTOGRAM_SIZE_SMALL,
  PICTOGRAM_SIZE_XLARGE,
  PICTOGRAM_SIZE_XSMALL
} from "./constants/pictogram-constants";
import { SvgLoader } from "../../common/svg-loader";
import { SvgType } from "../../common/svg-type";
import pfx from "../../utils/style-class-prefix-helper";
import { PictogramSchemeConfiguration } from "../../constants/colors";
import { setCssVariable } from "../../utils/css-variables";

@Component({
  tag: "eon-ui-pictogram",
  styleUrl: "pictogram.scss",
  shadow: true
})
export class Pictogram implements ColorableComponent<PictogramSchemeConfiguration<PictogramSchemeDefinition>> {
  schemeConfig: PictogramSchemeConfiguration<PictogramSchemeDefinition> = PICTOGRAM_SCHEME_CONFIG;
  schemeMap = PICTOGRAM_SCHEME_MAP;
  pictogramWrapper: HTMLElement;

  private svgType: SvgType = SvgType.PICTOGRAM;
  private interactionObserver?: IntersectionObserver;
  private readonly loader: SvgLoader;

  @State() private svgContent?: string;
  @State() private isVisible = false;
  @State() activeScheme: PictogramSchemeDefinition;

  @Element() hostElement: HTMLEonUiPictogramElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: PictogramSchemeName;

  /**
   * Specifies the label to use for accessibility. Defaults to the icon name.
   */
  @Prop({ mutable: true, reflect: true }) ariaLabel: string;

  /**
   * Specifies which icon to use from the built-in set of icons.
   */
  @Prop() name: string;

  /**
   * Specifies the vertical position of the icon inside the wrapper.
   * Possible Options are center, bottom.
   * Default is center.
   */
  @Prop() alignmentY: "center" | "bottom" = "center";

  /**
   * Specifies the horizontal position of the icon inside the wrapper.
   * Possible Options are left, center, right.
   * Default is center.
   */
  @Prop() alignmentX: "center" | "left" | "right" = "center";

  /**
   * A boolean that specifies whether or not the icon does have space around inside the wrapper.
   * Default is true.
   */
  @Prop() noSpace: boolean = true;

  /**
   * If enabled, the icon will be loaded lazily when it's visible in the viewport. Defaults to false.
   */
  @Prop() lazy = false;

  /**
   * Size of pictogram
   * @default PICTOGRAM_SIZE_NORMAL
   */
  @Prop() size: PictogramSize = PICTOGRAM_SIZE_NORMAL;

  constructor() {
    this.loader = new SvgLoader();
  }

  componentWillLoad() {
    this.applyScheme();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.delegateSvgWidthVariable();
  }

  connectedCallback() {
    // Purposely do not return the promise here because loading the SVG file should not hold up loading the app.
    // Only load the SVG if it's visible.
    this.waitUntilVisible(this.hostElement, "96px", () => {
      this.isVisible = true;
      this.loadPictogram();
    });
  }

  disconnectedCallback() {
    /* istanbul ignore next */
    if (this.interactionObserver) {
      this.interactionObserver.disconnect();
      this.interactionObserver = undefined;
    }
  }

  private waitUntilVisible(element: HTMLElement, rootMargin: string, callback: () => void) {
    /* istanbul ignore next */
    if (this.lazy && window && window.IntersectionObserver) {
      const io = (this.interactionObserver = new window.IntersectionObserver(
        (data: IntersectionObserverEntry[]) => {
          if (data[0].isIntersecting) {
            io.disconnect();
            this.interactionObserver = undefined;
            callback();
          }
        },
        { rootMargin }
      ));
      io.observe(element);
    } else {
      // browser doesn't support IntersectionObserver, so just show the icon
      callback();
    }
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("name")
  loadPictogram() {
    if (this.isVisible && !!this.name) {
      // Load SVG content
      this.svgContent = this.loader.getSvg(this.name, this.svgType);
      /* istanbul ignore next */
      if (!this.svgContent) {
        // async if it hasn't been loaded
        void this.loader.requestSvg(this.name, this.svgType).then(() => {
          this.svgContent = this.loader.getSvg(this.name, this.svgType);
        });
      }
    }
    this.ariaLabel = this.ariaLabel || this.name.toLowerCase().replace(/\-/g, " ");
  }

  /**
   *
   * Get the pictogram-svg width and make it available as a scss variable
   */
  @Watch("size")
  @Watch("name")
  private delegateSvgWidthVariable(): void {
    if (this.pictogramWrapper?.children[0]) {
      const svgWidth = this.pictogramWrapper.children[0].getAttribute("width");
      setCssVariable(this.hostElement, "--pictogramWidth", svgWidth);
    }
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "pictogram-wrapper": true,
            "no-spacing": this.noSpace,
            "y-center": this.alignmentY === "center",
            "y-bottom": this.alignmentY === "bottom",
            "x-left": this.alignmentX === "left",
            "x-center": this.alignmentX === "center",
            "x-right": this.alignmentX === "right",
            small: this.size === PICTOGRAM_SIZE_SMALL,
            xsmall: this.size === PICTOGRAM_SIZE_XSMALL,
            large: this.size === PICTOGRAM_SIZE_LARGE,
            xlarge: this.size === PICTOGRAM_SIZE_XLARGE
          })}
          ref={(el) => (this.pictogramWrapper = el as HTMLElement)}
          innerHTML={this.svgContent}
        ></div>
      </Host>
    );
  }
}
